import {
  action,
  computed,
  IObservableArray,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import { AssetsApi } from '../../api/assets';
import { ApiReq, emptyValue } from '../../api';
import {
  AssetCreationType,
  AssetFiltersType,
  AssetFormValue,
  AssetItem,
  AssetsResponse,
} from 'src/api/api-types/assets';
import { lookupsStore } from '../../stores/lookups';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { OnStartEditType } from 'src/components/Table/types';
import {
  defaultAssetsSearch,
  initialAssetsData,
  visibilityDefaultState,
} from 'src/components/AssetsTable/helpers/common';
import { returnIds } from 'src/utils/common';
import { ExportDataType } from 'src/api/exportTypes';
import { TableFilter } from 'src/models/filter/table-filter';
import { AssetModification } from 'src/models/assets/asset-modification';
import { Asset } from 'src/models/assets/asset';
import {
  AssetsExportColumnsNameMap,
  assetsFilterNameMap,
  AssetsNameMapType,
} from 'src/models/filter/constants';
import { TablePagination } from '../pagination/pagination';
import { GridColumnsManager } from '../grid-columns-manager/grid-columns-manager';
import { columns } from '../../components/AssetsTable/helpers/gridColumns';
import { userStore } from '../../stores/user';
import { AsyncExportEntity } from '../export/AsyncExportEntity';
import { ExportTypes } from 'src/types';
import {
  CommunicationTypeId,
  WaiverTypeId,
} from '../../components/AssetsTable/components/CreateActivity/type';
import { AllOrMyType } from '../calendar/calendar-model';
import { GalleryFileCollection } from '../file/gallery-collection';
import { globalViewModelStore } from '../../stores/global-vm';

export class AssetsModel {
  api = new AssetsApi({ prefix: '' });
  @observable isFilterOpen = false;
  @observable isCreation = false;
  @observable isEditing = false;

  @observable isWaiverCreation = false;
  @observable isCommunicationCreation = false;

  @observable deleteAssetModal = false;
  @observable selectedAssets: Array<Asset> = [];
  @observable exportEntity: AsyncExportEntity = new AsyncExportEntity();
  @observable gallery: GalleryFileCollection = new GalleryFileCollection();

  @observable.ref assetsReq: ApiReq<AssetsResponse> = emptyValue;
  @observable.ref assets: IObservableArray<Asset> = observable([]);
  @observable.ref chosenAsset?: AssetModification;
  @observable.ref activityEntityReq: ApiReq<AssetItem> = emptyValue;
  @observable pagination = new TablePagination<AssetsResponse>({
    requestRef: this.assetsReq,
  });

  @observable allOrMy = AllOrMyType.ALL;
  @observable columnsManager;

  @observable filterModel?: TableFilter<AssetFiltersType, AssetItem>;

  constructor() {
    makeObservable(this);
    this.pagination.setFetchingCallBack(this.fetchAssets);

    this.columnsManager = new GridColumnsManager<Asset>({
      columns: columns as GridColDef[],
      columnsMap: this.columnsVisibleMap,
      visibilityModel: visibilityDefaultState,
    });

    reaction(
      () => [this.allOrMy],
      () => this.fetchAssets(),
    );
  }

  @computed get defaultFilterField() {
    if (userStore.isJurisdiction) {
      delete defaultAssetsSearch['q.jurisdiction.id'];
      return defaultAssetsSearch;
    }
    return defaultAssetsSearch;
  }

  @computed get columnsVisibleMap() {
    return {
      jurisdiction: {
        jurisdiction: false,
      },
    };
  }

  @computed get filter() {
    this.filterModel ??= new TableFilter<AssetFiltersType, AssetItem>(
      this.columnsManager?.gridColumns,
      this.defaultFilterField,
      assetsFilterNameMap,
      (column, filters) => this.api.getAssetsFilterOptions({ filters, column }),
    );
    return this.filterModel;
  }

  @computed get assetsData() {
    if (this.assetsReq.state !== 'fulfilled' || !this.assetsReq.value) {
      return [];
    }

    return this.assetsReq.value.data?.items || [];
  }

  @computed get totalGenerators() {
    if (this.assetsReq.state !== 'fulfilled' || !this.assetsReq.value) {
      return 0;
    }
    return this.assetsReq.value.data?.totalGenerators || 0;
  }

  @computed get selectedActivityData() {
    if (
      this.activityEntityReq.state !== 'fulfilled' ||
      !this.activityEntityReq.value
    ) {
      return null;
    }

    return this.activityEntityReq.value.data || null;
  }

  @computed get isAssetsDisabledToDelete() {
    return this.selectedAssets.some(({ canUpdate }) => !canUpdate);
  }

  @action selectAsset = (id: number) => {
    const needle = this.assets.find(item => item.id === id);
    if (needle) this.chosenAsset = new AssetModification(needle);
  };

  @action startGallery = async (
    initFileId?: number,
    callback?: (model: AssetsModel) => void,
  ) => {
    if (!this.chosenAsset) {
      // eslint-disable-next-line no-console
      return console.log('no asset selected');
    }

    try {
      await this.gallery.initGallery(
        this.chosenAsset?.attachments.files,
        this.chosenAsset?.attachments.assetId,
        initFileId,
      );
      this.gallery.files.map(entity => entity.initPreview())
      await this.gallery.pickFile(initFileId);
      callback?.(this);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error in startGallery:', e);
      this.gallery.closeCarousel();
      throw e;
    }
  };

  @action modifyAsset = async (
    entity: AssetCreationType,
    old: AssetCreationType,
  ) => {
    const modifyAsset = new AssetModification(entity);
    const updateDescription = !(entity.description === old.description);
    if (!!modifyAsset) {
      try {
        await modifyAsset?.setEntity({
          ...entity,
          updateDescription,
          ...(entity?.type?.id === WaiverTypeId &&
            entity.waiverInfo && {
              waiverInfoStatusId: entity.waiverInfo.status.id,
              waiverInfoFollowUpDate: entity.waiverInfoFollowUpDate,
            }),
        });
        const updatedEntity = await modifyAsset?.editAsset();
        this.removeChosenAsset();
        entity.overdue = updatedEntity!.data!.overdue; //todo need fix without important
        await this.fetchAssets();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }
  };

  @action deleteAssets = () => {
    (async () => {
      await Promise.all(
        this.selectedAssets.map(async ({ id }) => {
          this.selectAsset(Number(id));
          await this.chosenAsset?.deleteAsset();
        }),
      );
      this.chosenAsset = undefined;
      this.removeSelectedAssets();
      await this.fetchAssets();
    })();
  };

  @action toggleEditAsset = (force?: boolean) => {
    this.isEditing = force !== undefined ? force : !this.isEditing;
  };

  @action addNewAsset = async () => {
    this.isCreation = true;
    Promise.allSettled([this.prepareAssetMainValues()]).then(_ => {
      this.chosenAsset = new AssetModification({ ...initialAssetsData });
    });
  };

  @action setNewAsset = (asset: AssetCreationType) => {
    this.isCreation = true;
    Promise.allSettled([this.prepareAssetMainValues()]).then(_ => {
      this.chosenAsset = new AssetModification(asset);
    });
  };

  //todo need refactoring
  @action setActivityType = (activityType?: number) => {
    if (activityType === CommunicationTypeId) {
      return (this.isCommunicationCreation = true);
    }
    if (activityType === WaiverTypeId) {
      return (this.isWaiverCreation = true);
    }
    return (this.isCreation = true);
  };

  @action setNewActivity = (
    asset: AssetCreationType,
    activityType?: number,
  ) => {
    Promise.allSettled([this.prepareAssetMainValues()])
      .then(_ => {
        this.chosenAsset = new AssetModification({
          ...asset,
          type: lookupsStore.assetTypes.find(item => item.id === activityType),
        });
      })
      //todo order is important
      .then(() => {
        this.setActivityType(activityType);
      });
  };

  @action prepareAssetMainValues = async () => {
    try {
      await lookupsStore.fetchAssetTypes();
    } catch (e) {
      throw e;
    }
  };

  @action fetchAssets = async () => {
    runInAction(() => {
      this.assetsReq = this.api.getAssets({
        ...this.pagination.serialize,
        filters: {
          ...this.filter.filterParams,
          'q.mode': this.allOrMy,
        },
      });
    });

    await this.assetsReq;

    runInAction(() => {
      if (
        this.assetsReq.state !== 'fulfilled' ||
        this.assetsReq.value?.data === null
      )
        return;
      this.assets.replace(
        this.assetsData.map(clientData => new Asset(clientData)),
      );
      this.pagination.setPagination(this.assetsReq.value?.data);
    });

    return this.assetsReq;
  };

  @action handleStartEditAction = async ({ id }: OnStartEditType<Asset>) => {
    this.selectAsset(Number(id));
    return Promise.allSettled([
      this.chosenAsset?.getFullDescription(),
      lookupsStore?.fetchAssetTypes(),
      lookupsStore?.initActivityCallsForEditing({
        activityTypeId: this.chosenAsset?.type?.id!,
        activityCategoryId: this.chosenAsset?.category?.id,
        activityRelatedToTypeId: this.chosenAsset?.relatedToType?.id,
        activityRelatedTo: this.chosenAsset?.relatedTo?.id,
      }),
    ]);
  };

  @action updateEditActivity = async (data: any) => {
    await this.chosenAsset?.setEntity({
      ...(data as unknown as Partial<AssetCreationType>),
      updateDescription: true,
    });
    await this.chosenAsset?.editAsset();
    await this.fetchAssets();
    this.toggleEditAsset(false);
    this.resetStore({ keepFilters: true });
  };

  @action editTargetActivity = async (id: number) => {
    const needle = this.assets.find(item => item.id === id);
    (async () => {
      try {
        if (!needle) return;
        this.toggleEditAsset(true);
        this.chosenAsset = new AssetModification({
          ...needle,
          assetDate: needle.assetDate
            ? new Date(needle.assetDate)
            : needle.assetDate,
          dueDate: needle.dueDate ? new Date(needle.dueDate) : needle.dueDate,
          dateIssued: needle.dateIssued ? new Date(needle.dateIssued) : null,
          waiverInfoStatusId: needle.waiverInfo
            ? needle.waiverInfo.status.id
            : undefined,
          waiverInfoFollowUpDate: needle.waiverInfo?.followUpDate
            ? new Date(needle.waiverInfo.followUpDate)
            : undefined,
        });
        return this.chosenAsset;
      } catch (e) {
        throw e;
      }
    })().then(() =>
      Promise.allSettled([
        this.chosenAsset?.getFullDescription(),
        lookupsStore?.fetchAssetTypes(),
        lookupsStore?.initActivityCallsForEditing({
          activityTypeId: this.chosenAsset?.type?.id!,
          activityCategoryId: this.chosenAsset?.category?.id,
          activityRelatedToTypeId: this.chosenAsset?.relatedToType?.id,
          activityRelatedTo: this.chosenAsset?.relatedTo?.id,
        }),
      ]),
    );
  };

  @action cancelEditingTargetActivity = () => {
    this.handleCancelEditAction();
    this.chosenAsset = undefined;
    this.toggleEditAsset(false);
  };

  @action handleCancelEditAction = () => {
    this.assets.replace(this.assets);
  };

  @action loadAttachments = (id: number) => {
    this.selectAsset(id);
    if (!this.chosenAsset) return;
    return this.chosenAsset.attachments.getAttachmentsList();
  };

  @action directCallCarousel = async (
    id: number,
    callback?: (model: AssetsModel) => void,
  ) => {
    try {
      await this.loadAttachments(id);
      if (!this.chosenAsset) return;
      const firstFileId = this.chosenAsset?.attachments.files[0]?.id;
      await this.startGallery(firstFileId, callback);
    } catch (error) {
      this.gallery.closeCarousel();
      globalViewModelStore.setApiErrors([
        { message: 'Server Error occurred', code: 'InternalServerError' },
      ]);
      // eslint-disable-next-line no-console
      console.error('Error in directCallCarousel:', error);

      throw error;
    }
  };

  @action removeChosenAsset = () => {
    this.chosenAsset = undefined;
  };

  @action setSelectedAssets = (items: GridRowId[]) => {
    const chosenAssets = this.assets.filter(({ id }) => items.includes(id));
    this.selectedAssets = [
      ...new Set([...chosenAssets, ...this.selectedAssets]),
    ].filter(({ id }) => items.includes(id));
  };

  @action removeSelectedAssets = () => (this.selectedAssets = []);

  @action fetchAssetsFile = async () => {
    if (!this.chosenAsset?.id) {
      throw new Error('cannot fetch Files, invalid assets id ');
    }
  };

  @action cancelCreation = () => {
    this.isCreation = false;
    this.isWaiverCreation = false;
    this.isCommunicationCreation = false;
    this.removeChosenAsset();
  };

  @action saveNewAsset = async (data: AssetFormValue) => {
    this.chosenAsset?.setCreationForm(data);
    const newAsset = await this.chosenAsset?.createAsset();

    if (!!newAsset) {
      this.chosenAsset = new AssetModification(
        newAsset.data as AssetCreationType,
      );
    }

    this.isCreation = false;
    this.isWaiverCreation = false;
    this.isCommunicationCreation = false;
    await this.fetchAssets();
  };

  @action saveAssetWithCb = async (
    data: AssetFormValue,
    callback: (asset: AssetModification) => void,
  ) => {
    await this.saveNewAsset(data);
    if (this.chosenAsset) callback(this.chosenAsset);
  };

  @action saveIRAsset = async (data: AssetFormValue) => {
    this.chosenAsset?.setCreationForm(data);
    await this.chosenAsset?.createIRAsset();
    this.removeChosenAsset();
    this.isCreation = false;
  };

  @action uploadAssets = (id: number) => {
    this.selectAsset(id);
  };

  @action toggleDeleteModal = (force: boolean) => {
    this.deleteAssetModal = force;
  };

  @action exportAction = async (data: ExportDataType) => {
    if (!data.type) return;
    if (Number(data.mode) === ExportTypes.ALL)
      this.exportEntity.setExportWithUnactiveRecords(true);
    this.exportEntity.setParams(data);

    const params = this.exportEntity.exportParams<
      AssetsResponse,
      AssetFiltersType,
      AssetItem
    >(
      this.columnsManager.hiddenColumns.map(el => {
        return AssetsExportColumnsNameMap[el as AssetsNameMapType] || el;
      }),
      this.filter,
      this.pagination,
      returnIds(this.selectedAssets),
    );
    //todo need fix need normal mechanism
    if (Number(data.mode) !== ExportTypes.ALL) {
      params.q = {
        ...params.q,
        mode: this.allOrMy,
      } as any;
    }

    if (this.filter.filterFields['q.implementationRecordSubCategory.id']) {
      params.q = {
        ...params.q,
        implementationRecordSubCategory: {
          id: this.filter.filterFields['q.implementationRecordSubCategory.id'],
        },
      } as any;
    }

    await this.exportEntity.asyncExportAction(
      this.api.getAsyncReportByType(params, data.type),
    );
  };
  @action switchTargetScope = (value: AllOrMyType) => {
    this.allOrMy = value;
  };

  @action closeGallery = () => {
    this.removeChosenAsset();
    this.gallery = new GalleryFileCollection();
  };

  @action resetStore = (options?: { keepFilters?: boolean }) => {
    const keepFilters = options?.keepFilters ?? false;

    this.pagination.resetPagination();
    if (!keepFilters) {
      this.filter.resetFilters();
    }
    this.filter.setSearchOpen(false);
    this.removeSelectedAssets();
    this.allOrMy = AllOrMyType.ALL;
  };
}
